import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api, { fmtUSD, fmtPct } from "@/lib/api";
import { Metric, Panel, Money, Pct, StatusBadge } from "@/components/terminal";
import { PieChart as PieIcon } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";

const GOLD = "#D4AF37";
const SLICES = ["#D4AF37", "#8A7326", "#4D7C8A", "#3F6B54", "#7A5C3E", "#5B5F6B", "#8C4A4A"];

export default function Overview() {
  const { dash } = useOutletContext();
  const [d, setD] = useState(dash);
  useEffect(() => { api.get("/dashboard").then((r) => setD(r.data)).catch(() => {}); }, []);
  const data = d || dash;
  if (!data) return <div className="text-slate-500 font-mono text-sm">Loading treasury state…</div>;

  const allocData = (data.allocations || []).map((a) => ({ name: a.name, value: a.target_pct }));

  return (
    <div className="space-y-5" data-testid="overview-page">
      <div className="panel-elevated p-5 sm:p-6 border-l-2 border-l-[#D4AF37]" data-testid="hero-net-treasury-card">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-2">
            <div className="metric-label">Net Treasury Value After Estimated Tax Liability</div>
            <div className="font-mono text-4xl sm:text-5xl font-extrabold tabular-nums gold-text mt-2" data-testid="metric-net-treasury">
              {fmtUSD(data.net_treasury_value)}
            </div>
            <div className="mt-2 flex items-center gap-3 text-xs text-slate-500">
              <span>Today <Pct value={data.today_change_pct} /></span>
              <span>All-time investment return <Pct value={data.all_time_return_pct} /></span>
              <StatusBadge status={data.execution_mode} testId="overview-mode-badge" />
            </div>
          </div>
          <div className="space-y-2 text-sm border-l border-[#2D323E] pl-5">
            <div className="flex justify-between"><span className="text-slate-500">Gross Treasury</span><Money value={data.gross_treasury_value} /></div>
            <div className="flex justify-between"><span className="text-slate-500">Estimated Tax Liability</span><Money value={-data.estimated_tax_liability} tone="neg" /></div>
            <div className="flex justify-between border-t border-[#2D323E] pt-2"><span className="text-slate-300 font-semibold">Net Treasury</span><Money value={data.net_treasury_value} className="gold-text font-bold" /></div>
          </div>
          <div className="space-y-2 text-sm border-l border-[#2D323E] pl-5">
            <div className="flex justify-between"><span className="text-slate-500">Tax Reserve Funded</span>
              {data.estimated_tax_liability > 0 ? (
                <span className={`font-mono tabular-nums ${data.tax_reserve_underfunded ? "text-amber-400" : "text-emerald-400"}`} data-testid="metric-tax-funded-pct">
                  {data.tax_reserve_funded_pct.toFixed(0)}%</span>
              ) : (
                <span className="text-emerald-400 text-[11px] font-semibold tracking-wide" data-testid="metric-tax-empty-state">NO CURRENT TAX LIABILITY</span>
              )}</div>
            <div className="flex justify-between"><span className="text-slate-500">Tax Reserve Balance</span><Money value={data.cash_reserved_taxes} /></div>
            <div className="flex justify-between"><span className="text-slate-500">Deployable Capital</span><Money value={data.deployable_capital} /></div>
            {data.tax_reserve_underfunded && <div className="text-amber-400 text-[11px] font-semibold" data-testid="overview-tax-warning">WARNING: tax reserve coverage below 100%</div>}
          </div>
        </div>
      </div>

      <Panel title="Capital Performance Decomposition" testId="capital-decomposition-panel">
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
          <Metric label="Capital Contributed" value={fmtUSD(data.net_external_capital)} testId="decomp-capital-contributed" />
          <Metric label="Investment Value Created" value={fmtUSD(data.investment_value_created)} tone={data.investment_value_created >= 0 ? "pos" : "neg"} testId="decomp-value-created" />
          <Metric label="Realized P/L" value={fmtUSD(data.realized_pl)} tone={data.realized_pl >= 0 ? "pos" : "neg"} testId="decomp-realized-pl" />
          <Metric label="Unrealized P/L" value={fmtUSD(data.unrealized_pl)} tone={data.unrealized_pl >= 0 ? "pos" : "neg"} testId="decomp-unrealized-pl" />
          <Metric label="Yield / Rewards" value={fmtUSD(data.income_generated)} tone={data.income_generated > 0 ? "pos" : undefined} testId="decomp-yield-rewards" />
          <Metric label="Tax Liability" value={fmtUSD(-data.estimated_tax_liability)} tone={data.estimated_tax_liability > 0 ? "neg" : undefined} testId="decomp-tax-liability" />
          <Metric label="Net NAV" value={fmtUSD(data.net_treasury_value)} testId="decomp-net-nav" />
        </div>
        <p className="text-[10px] text-slate-600 mt-3">External deposits and withdrawals are never counted as investment performance. Investment Value Created = Net NAV − net external capital contributed.</p>
      </Panel>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
        <Metric label="Total Treasury Value" value={fmtUSD(data.gross_treasury_value)} testId="metric-total-treasury" />
        <Metric label="Today's Change" value={fmtUSD(data.today_change)} sub={fmtPct(data.today_change_pct)} tone={data.today_change >= 0 ? "pos" : "neg"} testId="metric-today-change" />
        <Metric label="Cash Available" value={fmtUSD(data.cash_available)} testId="metric-cash-available" />
        <Metric label="Cash Reserved For Taxes" value={fmtUSD(data.cash_reserved_taxes)} testId="metric-cash-tax-reserve" />
        <Metric label="Crypto Value" value={fmtUSD(data.crypto_value)} testId="metric-crypto-value" />
        <Metric label="Traditional Asset Value" value={fmtUSD(data.traditional_value)} testId="metric-traditional-value" />
        <Metric label="Staked Asset Value" value={fmtUSD(data.staked_value)} testId="metric-staked-value" />
        <Metric label="Tax Liability Estimate" value={fmtUSD(data.estimated_tax_liability)} tone={data.estimated_tax_liability > 0 ? "neg" : undefined} testId="metric-tax-liability" />
        <Metric label="Opportunity Reserve" value={fmtUSD(data.opportunity_reserve)} testId="metric-opportunity-reserve" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Panel title="Target Allocation" testId="overview-allocation-chart" className="lg:col-span-1">
          <div className="h-56 w-full" style={{ minHeight: 224 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={allocData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={85} stroke="#0B0C0E" strokeWidth={2} isAnimationActive={false}>
                  {allocData.map((_, i) => <Cell key={i} fill={SLICES[i % SLICES.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#17191E", border: "1px solid #2D323E", fontSize: 12 }}
                  formatter={(v, n) => [`${v}%`, n]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2">
            {allocData.map((a, i) => (
              <div key={a.name} className="flex items-center gap-2 text-[11px] text-slate-400">
                <span className="w-2 h-2 rounded-sm shrink-0" style={{ background: SLICES[i % SLICES.length] }} />
                <span className="truncate">{a.name}</span>
                <span className="ml-auto font-mono">{a.value}%</span>
              </div>
            ))}
          </div>
        </Panel>

        <Panel title="Positions" testId="overview-positions" className="lg:col-span-1">
          {data.positions.length === 0 ? (
            <div className="text-slate-500 text-sm flex flex-col items-center gap-2 py-6">
              <PieIcon className="w-6 h-6" /> No asset positions yet. Treasury is 100% cash.
            </div>
          ) : (
            <div className="space-y-2">
              {data.positions.map((p) => (
                <div key={p.asset} className="flex items-center justify-between text-sm border-b border-[#222630] pb-2">
                  <span className="font-semibold text-slate-200">{p.asset}</span>
                  <span className="font-mono tabular-nums text-slate-400">{p.quantity.toFixed(6)}</span>
                  <Money value={p.value} />
                  <Money value={p.unrealized_pl} tone={p.unrealized_pl >= 0 ? "pos" : "neg"} />
                </div>
              ))}
            </div>
          )}
        </Panel>

        <Panel title="Capital Buckets" testId="overview-buckets" className="lg:col-span-1">
          <div className="space-y-2">
            {data.buckets.map((b) => (
              <div key={b.key} className="flex items-center justify-between text-sm border-b border-[#222630] pb-1.5 last:border-0">
                <span className="text-slate-300">{b.name}{b.key === "tax_reserve" && <span className="ml-2 text-[10px] text-amber-500 uppercase tracking-wider">Locked</span>}</span>
                <Money value={b.balance} className={b.key === "tax_reserve" ? "text-amber-400" : ""} />
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
}
